import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "@phosphor-icons/react";
import { MaskedLine } from "./Reveal";
import { IMAGES } from "../data/content";
import { scrollToId } from "../lib/api";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.22]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden pt-[72px]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 w-full grid lg:grid-cols-12 gap-12 items-center py-16">
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <div className="overflow-hidden mb-8">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="uppercase tracking-[0.25em] text-xs font-bold text-accent font-sans"
              data-testid="hero-eyebrow"
            >
              Independent education for vacation owners
            </motion.p>
          </div>
          <h1 className="font-serif text-primary tracking-tight leading-[1.02] text-[13vw] sm:text-[11vw] lg:text-[6.2vw]" data-testid="hero-headline">
            <MaskedLine delay={0.25}>Buy with</MaskedLine>
            <MaskedLine delay={0.4}><em className="text-accent">conviction.</em></MaskedLine>
            <MaskedLine delay={0.55}>Sell without</MaskedLine>
            <MaskedLine delay={0.7}><em className="text-accent">regret.</em></MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-10 max-w-xl text-foreground/70 text-base md:text-lg"
            data-testid="hero-subtext"
          >
            A clear, independent field guide to vacation ownership — when adding points makes sense,
            how to sell or exit safely, and how to tell a trustworthy company from a costly scripted sales presentation.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2 }}
            className="mt-12 flex flex-wrap items-center gap-5"
          >
            <button
              data-testid="hero-cta-quiz"
              onClick={() => scrollToId("decision-tool")}
              className="group flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 rounded-full font-semibold text-sm tracking-wide hover:bg-accent transition-colors duration-300"
            >
              Take the 60-second decision tool
              <ArrowUpRight size={18} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </button>
            <button
              data-testid="hero-cta-guides"
              onClick={() => scrollToId("guides")}
              className="text-sm font-semibold text-primary border-b-2 border-accent pb-1 hover:text-accent transition-colors duration-300"
            >
              Read the chapters
            </button>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative hidden lg:block">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.3, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative overflow-hidden rounded-sm aspect-[3/4] max-w-[440px] ml-auto border border-border"
            data-testid="hero-image-frame"
          >
            <motion.img
              src={IMAGES.hero}
              alt="Abstract architectural lines in soft light"
              style={{ scale: imgScale, y: imgY }}
              className="w-full h-full object-cover"
              data-testid="hero-image"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 1.35 }}
            className="absolute -left-10 bottom-12 bg-card border border-border px-6 py-5 rounded-sm max-w-[240px]"
            data-testid="hero-stat-card"
          >
            <p className="font-serif text-4xl text-accent">85%</p>
            <p className="text-xs font-semibold text-foreground/60 mt-1 leading-relaxed">
              of buyers regretted the purchase in an independent UCF study — the industry's own survey claims the opposite. Education changes the odds.
            </p>
          </motion.div>
        </div>
      </div>

      <motion.button
        data-testid="hero-scroll-hint"
        onClick={() => scrollToId("guides")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-6 md:left-12 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-foreground/50 hover:text-primary transition-colors duration-300"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ArrowDown size={16} weight="bold" />
        </motion.span>
        Scroll
      </motion.button>
    </section>
  );
}
